import type { DentalInstance } from './types';

export const PALETTES = {
  soft: ['#7798C4', '#75A99C', '#C68E8D', '#BA9DC4', '#C5A773', '#89ACC1', '#A6AE7B', '#C29BA8', '#8C9CC0', '#BAA28E', '#80B3B4', '#B5A4C7'],
  pastel: ['#A0BCE0', '#A0CBBE', '#DDAFAC', '#C8B3DE', '#DDC18E', '#A9CBDB', '#C2CDA1', '#D6B4C6', '#ADB8DB', '#D1BBA5', '#9CCBCC', '#C4BADB'],
  contrast: ['#648CC5', '#4F9D91', '#CC806D', '#A386BC', '#C0A04C', '#5D9DB7', '#8A9D58', '#BB7A9F', '#727BB0', '#AD8764', '#56A8B2', '#A898CA'],
} as const;
// Stable raw/FDI identities: avoid similar hues along the arch, across the
// midline and between opposing teeth (including the immediate opposing peers).
// Colored jaws remain visible against the grayscale CT and tooth colors.
export const ANATOMY_CONTRAST_COLORS: Record<number, string> = {
  1: '#3DA7F2', 2: '#FFA58C',
  11: '#FFC105', 12: '#FF33CC', 13: '#99FF33', 14: '#0544FF',
  15: '#82D92B', 16: '#9933FF', 17: '#2BD9D9', 18: '#FF0505',
  21: '#8205FF', 22: '#05FFFF', 23: '#822BD9', 24: '#FFCC33',
  25: '#D904A4', 26: '#05FF44', 27: '#04D9D9', 28: '#FF05C1',
  31: '#FF3333', 32: '#6FD904', 33: '#D90404', 34: '#2BD957',
  35: '#3366FF', 36: '#D9AD2B', 37: '#0439D9', 38: '#82FF05',
  41: '#33FF66', 42: '#2B57D9', 43: '#D9A404', 44: '#D92BAD',
  45: '#33FFFF', 46: '#D92B2B', 47: '#04D939', 48: '#6F04D9',
};
export type Palette = keyof typeof PALETTES | 'annotation' | 'custom';
export type Visibility = 'individual' | 'all' | 'none';
export interface AppearancePreferences {
  theme: 'light' | 'dark';
  palette: Palette;
  outlineWidth: number;
  fillOpacity: number;
  visibility: Visibility;
  customColors: Record<string, string>;
  visibleOverrides: Record<string, boolean>;
  sampleVisibility: Record<string, Visibility>;
}
export const defaultAppearance = (): AppearancePreferences => ({
  theme: 'light', palette: 'contrast', outlineWidth: 1, fillOpacity: 0.25,
  visibility: 'individual', customColors: {}, visibleOverrides: {}, sampleVisibility: {},
});
export function normalizeHex(value: string) {
  const hex = value.trim().toUpperCase();
  if (/^#[0-9A-F]{6}$/.test(hex)) return hex;
  if (/^#[0-9A-F]{3}$/.test(hex)) return `#${[...hex.slice(1)].map(c => c + c).join('')}`;
  return null;
}
function finiteRange(value: unknown, fallback: number, max: number) {
  return typeof value === 'number' && Number.isFinite(value) ? Math.max(0, Math.min(max, value)) : fallback;
}
export function readAppearance(value: unknown): AppearancePreferences {
  const defaults = defaultAppearance();
  if (!value || typeof value !== 'object' || Array.isArray(value)) return defaults;
  const input = value as Partial<AppearancePreferences>;
  const colors = input.customColors && typeof input.customColors === 'object' ? input.customColors : {};
  const visible = input.visibleOverrides && typeof input.visibleOverrides === 'object' ? input.visibleOverrides : {};
  return {
    theme: input.theme === 'dark' ? 'dark' : 'light',
    palette: ['soft', 'pastel', 'contrast', 'annotation', 'custom'].includes(input.palette || '') ? input.palette! : defaults.palette,
    outlineWidth: Math.round(finiteRange(input.outlineWidth, 1, 6)),
    fillOpacity: finiteRange(input.fillOpacity, 0.25, 1),
    visibility: ['individual', 'all', 'none'].includes(input.visibility || '') ? input.visibility! : 'individual',
    customColors: Object.fromEntries(Object.entries(colors).flatMap(([id, hex]) => {
      const parsed = typeof hex === 'string' ? normalizeHex(hex) : null;
      return parsed ? [[id, parsed]] : [];
    })),
    visibleOverrides: Object.fromEntries(Object.entries(visible).filter(([, flag]) => typeof flag === 'boolean')),
    sampleVisibility: Object.fromEntries(Object.entries(input.sampleVisibility || {}).filter(([, mode]) => ['individual','all','none'].includes(mode))),
  };
}
export function displayColor(row: Pick<DentalInstance, 'id' | 'labelValue' | 'color'> & { fdi?: number | null }, preferences: AppearancePreferences): DentalInstance['color'] {
  if (preferences.palette === 'annotation') return [...row.color];
  const paletteName = preferences.palette === 'custom' ? 'contrast' : preferences.palette;
  const palette = PALETTES[paletteName];
  const anatomyColor = paletteName === 'contrast' ? ANATOMY_CONTRAST_COLORS[row.fdi ?? row.labelValue] : undefined;
  const hex = preferences.palette === 'custom' && preferences.customColors[row.id]
    ? preferences.customColors[row.id] : anatomyColor ?? palette[(row.labelValue - 1) % palette.length];
  return [1, 3, 5].map(offset => parseInt(hex.slice(offset, offset + 2), 16)).concat(row.color[3]) as DentalInstance['color'];
}
export function displayVisible(row: Pick<DentalInstance, 'id' | 'visible'>, preferences: AppearancePreferences) {
  if (preferences.visibility !== 'individual') return preferences.visibility === 'all';
  return preferences.visibleOverrides[row.id] ?? row.visible;
}
export function colorHex(color: number[]) {
  return `#${color.slice(0, 3).map(value => value.toString(16).padStart(2, '0')).join('').toUpperCase()}`;
}
